import { useState, useRef, useEffect } from 'react'
import axios from 'axios'
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog'
import { Button } from './ui/button'
import { toast } from 'sonner'
import { useLanguage } from '../context/LanguageContext'
import { useOffline, EVIDENCE_STATUS } from '../context/OfflineContext'
import { EvidenceSyncStatus } from './OfflineBanner'
import { format, parseISO } from 'date-fns'
import { es, enUS } from 'date-fns/locale'
import { 
  Camera, Image as ImageIcon, Plus, X, Loader2, 
  ZoomIn, Trash2, Upload, AlertCircle, User, Calendar,
  CloudOff
} from 'lucide-react'

const API = `${process.env.REACT_APP_BACKEND_URL}/api`
const MAX_EVIDENCES = 10
const MAX_FILE_SIZE = 4 * 1024 * 1024 // 4MB max
const MAX_IMAGE_DIMENSION = 1600 // Compress to 1600px max

// Component to load authenticated images
function AuthenticatedImage({ src, alt, className, onClick }) {
  const [imageSrc, setImageSrc] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')

  useEffect(() => {
    if (!src) {
      setError(true)
      setErrorMsg('No URL provided')
      setLoading(false)
      return
    }

    const loadImage = async () => {
      setLoading(true)
      setError(false)
      setErrorMsg('')
      try {
        const response = await axios.get(src, { responseType: 'blob' })
        const blob = new Blob([response.data], { type: response.headers['content-type'] })
        const objectUrl = URL.createObjectURL(blob)
        setImageSrc(objectUrl)
      } catch (err) {
        console.error('Error loading image:', err, 'URL:', src)
        setError(true)
        setErrorMsg(err.response?.status === 404 ? 'File not found' : 
                    err.response?.status === 403 ? 'No access' : 
                    err.message || 'Load failed')
      } finally {
        setLoading(false)
      }
    }

    loadImage()

    // Cleanup blob URL on unmount
    return () => {
      if (imageSrc) {
        URL.revokeObjectURL(imageSrc)
      }
    }
  }, [src])

  if (loading) {
    return (
      <div className={`${className} flex items-center justify-center bg-muted`}>
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (error || !imageSrc) {
    return (
      <div className={`${className} flex flex-col items-center justify-center bg-muted gap-1 p-2`}>
        <AlertCircle className="w-5 h-5 text-muted-foreground" />
        {errorMsg && <span className="text-xs text-muted-foreground text-center line-clamp-2">{errorMsg}</span>}
      </div>
    )
  }

  return (
    <img
      src={imageSrc}
      alt={alt}
      className={className}
      onClick={onClick}
    />
  )
}

export function TaskEvidencesLoader({ taskId, readOnly = false, onCreateTask = null }) {
  const { locale } = useLanguage()
  const { isOnline, isDBReady, saveEvidenceOffline, getLocalEvidences, retryEvidence } = useOffline()
  const lang = locale.startsWith('es') ? 'es' : 'en'
  const dateLocale = lang === 'es' ? es : enUS
  
  const [evidences, setEvidences] = useState([])
  const [localEvidences, setLocalEvidences] = useState([])
  const [pendingUploads, setPendingUploads] = useState([]) // Optimistic uploads with local preview
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [showOptions, setShowOptions] = useState(false)
  const [lightboxImage, setLightboxImage] = useState(null)
  const [deletingId, setDeletingId] = useState(null)
  const [currentTaskId, setCurrentTaskId] = useState(taskId)
  
  const fileInputRef = useRef(null)
  const cameraInputRef = useRef(null)

  // Update currentTaskId when prop changes
  useEffect(() => {
    if (taskId) {
      setCurrentTaskId(taskId)
    }
  }, [taskId])

  // Fetch on mount and when taskId changes
  useEffect(() => {
    if (currentTaskId) {
      fetchEvidences()
      fetchLocalEvidences()
    } else {
      setLoading(false)
    }
  }, [currentTaskId])
  
  // Re-fetch when coming online
  useEffect(() => {
    if (isOnline && currentTaskId) {
      fetchEvidences()
    }
  }, [isOnline, currentTaskId])

  const fetchEvidences = async () => {
    if (!currentTaskId) return
    if (!isOnline) {
      setLoading(false)
      return
    }
    
    setLoading(true)
    try {
      const response = await axios.get(`${API}/tasks/${currentTaskId}/evidences`)
      setEvidences(response.data)
    } catch (error) {
      console.error('Error fetching evidences:', error)
    } finally {
      setLoading(false)
    }
  }
  
  const fetchLocalEvidences = async () => {
    if (!isDBReady || !currentTaskId) return
    try {
      const local = await getLocalEvidences(currentTaskId)
      setLocalEvidences(local)
    } catch (error) {
      console.error('Error fetching local evidences:', error)
    }
  }

  // Compress image before upload
  const compressImage = (file) => {
    return new Promise((resolve) => {
      // Skip compression for small files
      if (file.size <= MAX_FILE_SIZE) {
        resolve(file)
        return
      }
      
      const reader = new FileReader()
      reader.onload = (e) => {
        const img = new Image()
        img.onload = () => {
          const canvas = document.createElement('canvas')
          let { width, height } = img
          
          // Scale down if needed
          if (width > MAX_IMAGE_DIMENSION || height > MAX_IMAGE_DIMENSION) {
            if (width > height) {
              height = (height / width) * MAX_IMAGE_DIMENSION
              width = MAX_IMAGE_DIMENSION
            } else {
              width = (width / height) * MAX_IMAGE_DIMENSION
              height = MAX_IMAGE_DIMENSION
            }
          }
          
          canvas.width = width
          canvas.height = height
          
          const ctx = canvas.getContext('2d')
          ctx.drawImage(img, 0, 0, width, height)
          
          canvas.toBlob(
            (blob) => {
              if (blob) {
                const compressedFile = new File([blob], file.name, {
                  type: 'image/jpeg',
                  lastModified: Date.now()
                })
                resolve(compressedFile)
              } else {
                resolve(file)
              }
            },
            'image/jpeg',
            0.85 // Quality
          )
        }
        img.src = e.target.result
      }
      reader.readAsDataURL(file)
    })
  }

  const handleUpload = async (files) => {
    if (!files || files.length === 0) return
    
    const totalEvidences = evidences.length + localEvidences.filter(e => e.evidence_status !== EVIDENCE_STATUS.SYNCED).length + pendingUploads.length
    const remainingSlots = MAX_EVIDENCES - totalEvidences
    if (remainingSlots <= 0) {
      toast.error(lang === 'es' ? `Máximo ${MAX_EVIDENCES} evidencias por tarea` : `Maximum ${MAX_EVIDENCES} evidences per task`)
      return
    }
    
    const filesToUpload = Array.from(files).slice(0, remainingSlots)
    
    // Close the options dialog IMMEDIATELY - don't wait for upload
    setShowOptions(false)
    
    // If no taskId yet, we need to create the task first
    let uploadTaskId = currentTaskId
    if (!uploadTaskId && onCreateTask) {
      try {
        const result = await onCreateTask()
        // onCreateTask returns { success: true, id } or { success: false, reason }
        // or just the id for backwards compatibility
        if (result && typeof result === 'object' && result.success) {
          uploadTaskId = result.id
          setCurrentTaskId(result.id)
        } else if (result && typeof result === 'string') {
          // Backwards compatibility: onCreateTask returned just the id
          uploadTaskId = result
          setCurrentTaskId(result)
        } else {
          // Validation failed in parent - toast was already shown there
          // Don't show another generic error toast
          return
        }
      } catch (error) {
        console.error('Error creating task:', error)
        toast.error(lang === 'es' ? 'Error al crear la tarea' : 'Error creating task')
        return
      }
    }
    
    if (!uploadTaskId) {
      toast.error(lang === 'es' ? 'Primero guarda la tarea' : 'Save the task first')
      return
    }
    
    // Create optimistic placeholders for each file IMMEDIATELY
    const newPendingUploads = []
    for (const file of filesToUpload) {
      if (!file.type.startsWith('image/')) {
        toast.error(lang === 'es' ? 'Solo se permiten imágenes' : 'Only images allowed')
        continue
      }
      
      const localId = `pending_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      const localUrl = URL.createObjectURL(file)
      
      newPendingUploads.push({
        id: localId,
        local_url: localUrl,
        filename: file.name,
        status: 'uploading', // uploading | done | error
        file: file // Keep reference for retry
      })
    }
    
    // Add placeholders to state immediately (UI updates right away)
    setPendingUploads(prev => [...prev, ...newPendingUploads])
    
    // Now upload each file in background (async, non-blocking)
    for (const pending of newPendingUploads) {
      // Use setTimeout to ensure UI updates first
      setTimeout(() => {
        uploadSingleFile(pending, uploadTaskId)
      }, 0)
    }
  }
  
  // Upload a single file async
  const uploadSingleFile = async (pending, uploadTaskId) => {
    try {
      // Compress the image
      const compressedFile = await compressImage(pending.file)
      
      if (compressedFile.size > MAX_FILE_SIZE) {
        // Update to error state
        setPendingUploads(prev => prev.map(p => 
          p.id === pending.id 
            ? { ...p, status: 'error', error: lang === 'es' ? 'Archivo muy grande' : 'File too large' }
            : p
        ))
        return
      }
      
      // OFFLINE MODE: Save locally
      if (!isOnline && isDBReady) {
        const localEvidence = await saveEvidenceOffline(uploadTaskId, compressedFile)
        if (localEvidence) {
          setLocalEvidences(prev => [...prev, localEvidence])
          // Remove from pending
          setPendingUploads(prev => prev.filter(p => p.id !== pending.id))
          toast.success(lang === 'es' ? 'Guardado localmente' : 'Saved locally')
        }
        return
      }
      
      // ONLINE MODE: Upload to server
      const formData = new FormData()
      formData.append('file', compressedFile)
      
      const response = await axios.post(`${API}/tasks/${uploadTaskId}/evidences`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      
      // Success! Remove from pending and add to evidences
      setPendingUploads(prev => prev.filter(p => p.id !== pending.id))
      setEvidences(prev => [...prev, response.data])
      toast.success(lang === 'es' ? 'Evidencia subida' : 'Evidence uploaded')
      
    } catch (error) {
      console.error('Upload error:', error)
      
      // Fallback to offline storage on network error
      if (isDBReady && error.message?.includes('Network')) {
        try {
          const localEvidence = await saveEvidenceOffline(uploadTaskId, pending.file)
          if (localEvidence) {
            setLocalEvidences(prev => [...prev, localEvidence])
            setPendingUploads(prev => prev.filter(p => p.id !== pending.id))
            toast.info(lang === 'es' 
              ? 'Guardado localmente. Se subirá cuando vuelvas a estar en línea.'
              : 'Saved locally. Will upload when back online.')
            return
          }
        } catch (offlineError) {
          console.error('Offline fallback failed:', offlineError)
        }
      }
      
      // Update to error state
      setPendingUploads(prev => prev.map(p => 
        p.id === pending.id 
          ? { ...p, status: 'error', error: error.response?.data?.detail || (lang === 'es' ? 'Error al subir' : 'Upload error') }
          : p
      ))
    }
  }
  
  // Retry a failed upload
  const retryUpload = (pending) => {
    // Reset to uploading state
    setPendingUploads(prev => prev.map(p => 
      p.id === pending.id ? { ...p, status: 'uploading', error: null } : p
    ))
    // Retry upload
    setTimeout(() => {
      uploadSingleFile(pending, currentTaskId)
    }, 0)
  }
  
  // Remove a pending upload
  const removePendingUpload = (pendingId) => {
    setPendingUploads(prev => {
      const toRemove = prev.find(p => p.id === pendingId)
      if (toRemove?.local_url) {
        URL.revokeObjectURL(toRemove.local_url)
      }
      return prev.filter(p => p.id !== pendingId)
    })
  }

  const handleDelete = async (evidenceId, isLocal = false) => {
    // For local evidences, just remove from state
    if (isLocal || evidenceId.startsWith('local_')) {
      setLocalEvidences(prev => prev.filter(e => e.id !== evidenceId))
      toast.success(lang === 'es' ? 'Evidencia eliminada' : 'Evidence deleted')
      return
    }
    
    // For server evidences
    if (!isOnline) {
      toast.error(lang === 'es' ? 'No se puede eliminar sin conexión' : 'Cannot delete offline')
      return
    }
    
    setDeletingId(evidenceId)
    try {
      await axios.delete(`${API}/tasks/evidences/${evidenceId}`)
      toast.success(lang === 'es' ? 'Evidencia eliminada' : 'Evidence deleted')
      setEvidences(prev => prev.filter(e => e.id !== evidenceId))
    } catch (error) {
      toast.error(lang === 'es' ? 'Error al eliminar' : 'Delete error')
    } finally {
      setDeletingId(null)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  // Combine server and local evidences, filtering out synced locals
  const pendingLocalEvidences = localEvidences.filter(
    e => e.evidence_status !== EVIDENCE_STATUS.SYNCED
  )
  const totalCount = evidences.length + pendingLocalEvidences.length + pendingUploads.length

  return (
    <div className="space-y-4" data-testid="task-evidences">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <Camera className="w-4 h-4" />
          {lang === 'es' ? 'Evidencias' : 'Evidences'}
          <span className="text-muted-foreground font-normal">
            ({totalCount}/{MAX_EVIDENCES})
          </span>
          {!isOnline && (
            <CloudOff className="w-4 h-4 text-amber-500" />
          )}
        </h3>
        
        {!readOnly && totalCount < MAX_EVIDENCES && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              setShowOptions(true)
            }}
            disabled={uploading}
            className="rounded-full"
            data-testid="add-evidence-btn"
          >
            {uploading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {uploadProgress}%
              </>
            ) : (
              <>
                <Plus className="w-4 h-4 mr-2" />
                {lang === 'es' ? 'Agregar' : 'Add'}
              </>
            )}
          </Button>
        )}
      </div>

      {/* Evidence Grid */}
      {totalCount > 0 ? (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
          {/* Server evidences */}
          {evidences.map((evidence) => (
            <div
              key={evidence.id}
              className="relative group aspect-square rounded-lg overflow-hidden bg-muted"
              data-testid={`evidence-${evidence.id}`}
            >
              <AuthenticatedImage
                src={evidence.file_url}
                alt="Evidence"
                className="w-full h-full object-cover cursor-pointer transition-transform group-hover:scale-105"
                onClick={() => setLightboxImage(evidence)}
              />
              
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                <Button
                  variant="secondary"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setLightboxImage(evidence)}
                >
                  <ZoomIn className="w-4 h-4" />
                </Button>
                {!readOnly && isOnline && (
                  <Button
                    variant="destructive"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => handleDelete(evidence.id)}
                    disabled={deletingId === evidence.id}
                  >
                    {deletingId === evidence.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </Button>
                )}
              </div>
            </div>
          ))}
          
          {/* Local (pending) evidences */}
          {pendingLocalEvidences.map((evidence) => (
            <div
              key={evidence.id}
              className="relative group aspect-square rounded-lg overflow-hidden bg-muted"
              data-testid={`local-evidence-${evidence.id}`}
            >
              {evidence.local_url ? (
                <img
                  src={evidence.local_url}
                  alt="Local evidence"
                  className="w-full h-full object-cover cursor-pointer transition-transform group-hover:scale-105"
                  onClick={() => setLightboxImage({ ...evidence, file_url: evidence.local_url })}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-muted">
                  <ImageIcon className="w-8 h-8 text-muted-foreground/50" />
                </div>
              )}
              
              {/* Sync status overlay */}
              <EvidenceSyncStatus 
                status={evidence.evidence_status}
                onRetry={evidence.evidence_status === EVIDENCE_STATUS.FAILED 
                  ? () => retryEvidence(evidence.id) 
                  : null
                }
              />
              
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                <Button
                  variant="secondary"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setLightboxImage({ ...evidence, file_url: evidence.local_url })}
                >
                  <ZoomIn className="w-4 h-4" />
                </Button>
                {!readOnly && (
                  <Button
                    variant="destructive"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => handleDelete(evidence.id, true)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          ))}
          
          {/* Pending uploads (async placeholders) */}
          {pendingUploads.map((pending) => (
            <div
              key={pending.id}
              className="relative aspect-square rounded-lg overflow-hidden bg-muted"
              data-testid={`pending-upload-${pending.id}`}
            >
              {pending.local_url ? (
                <img
                  src={pending.local_url}
                  alt="Uploading..."
                  className={`w-full h-full object-cover ${pending.status === 'uploading' ? 'opacity-60' : ''}`}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-muted">
                  <ImageIcon className="w-8 h-8 text-muted-foreground/50" />
                </div>
              )}
              
              {/* Upload status overlay */}
              <div className="absolute inset-0 flex items-center justify-center">
                {pending.status === 'uploading' && (
                  <div className="bg-black/60 rounded-full p-2">
                    <Loader2 className="w-6 h-6 animate-spin text-white" />
                  </div>
                )}
                {pending.status === 'error' && (
                  <div className="absolute inset-0 bg-red-500/20 flex flex-col items-center justify-center gap-2 p-2">
                    <AlertCircle className="w-6 h-6 text-red-500" />
                    <p className="text-xs text-red-500 text-center line-clamp-2">{pending.error}</p>
                    <div className="flex gap-1">
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        className="h-7 px-2 text-xs"
                        onClick={() => retryUpload(pending)}
                      >
                        <Upload className="w-3 h-3 mr-1" />
                        {lang === 'es' ? 'Reintentar' : 'Retry'}
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-xs text-red-500"
                        onClick={() => removePendingUpload(pending.id)}
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 border-2 border-dashed rounded-lg">
          <Camera className="w-10 h-10 mx-auto text-muted-foreground/30 mb-2" />
          <p className="text-sm text-muted-foreground">
            {lang === 'es' ? 'Sin evidencias' : 'No evidences'}
          </p>
          {!readOnly && (
            <Button
              type="button"
              variant="link"
              size="sm"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                setShowOptions(true)
              }}
              className="mt-2"
            >
              {lang === 'es' ? 'Agregar primera evidencia' : 'Add first evidence'}
            </Button>
          )}
        </div>
      )}

      {/* Hidden file inputs */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={(e) => e.target.files && handleUpload(e.target.files)}
        className="hidden"
      />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={(e) => e.target.files && handleUpload(e.target.files)}
        className="hidden"
      />

      {/* Options Dialog */}
      <Dialog open={showOptions} onOpenChange={setShowOptions}>
        <DialogContent className="sm:max-w-xs" onPointerDownOutside={(e) => e.preventDefault()}>
          <DialogTitle className="sr-only">
            {lang === 'es' ? 'Agregar evidencia' : 'Add evidence'}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {lang === 'es' ? 'Selecciona cómo agregar evidencia' : 'Select how to add evidence'}
          </DialogDescription>
          <div className="space-y-3 py-4">
            <h3 className="text-center font-semibold">
              {lang === 'es' ? 'Agregar evidencia' : 'Add evidence'}
            </h3>
            
            <Button
              type="button"
              variant="outline"
              className="w-full h-16 justify-start gap-4"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                cameraInputRef.current?.click()
              }}
              data-testid="take-photo-btn"
            >
              <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                <Camera className="w-5 h-5 text-blue-500" />
              </div>
              <div className="text-left">
                <p className="font-medium">{lang === 'es' ? 'Tomar foto' : 'Take photo'}</p>
                <p className="text-xs text-muted-foreground">
                  {lang === 'es' ? 'Usar cámara' : 'Use camera'}
                </p>
              </div>
            </Button>
            
            <Button
              type="button"
              variant="outline"
              className="w-full h-16 justify-start gap-4"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                fileInputRef.current?.click()
              }}
              data-testid="choose-gallery-btn"
            >
              <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center">
                <ImageIcon className="w-5 h-5 text-emerald-500" />
              </div>
              <div className="text-left">
                <p className="font-medium">{lang === 'es' ? 'Elegir de galería' : 'Choose from gallery'}</p>
                <p className="text-xs text-muted-foreground">
                  {lang === 'es' ? 'Seleccionar fotos' : 'Select photos'}
                </p>
              </div>
            </Button>
            
            <p className="text-xs text-center text-muted-foreground">
              {lang === 'es' 
                ? `Puedes subir hasta ${MAX_EVIDENCES - totalCount} imagen(es) más` 
                : `You can upload up to ${MAX_EVIDENCES - totalCount} more image(s)`}
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Lightbox */}
      <Dialog open={!!lightboxImage} onOpenChange={() => setLightboxImage(null)}>
        <DialogContent className="max-w-4xl p-0 bg-black/95">
          <DialogTitle className="sr-only">
            {lang === 'es' ? 'Ver evidencia' : 'View evidence'}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {lang === 'es' ? 'Vista ampliada de la evidencia' : 'Full size evidence view'}
          </DialogDescription>
          {lightboxImage && (
            <div className="relative">
              <AuthenticatedImage
                src={lightboxImage.file_url}
                alt="Evidence"
                className="w-full h-auto max-h-[80vh] object-contain"
              />
              
              <Button
                variant="ghost"
                size="icon"
                className="absolute top-2 right-2 text-white hover:bg-white/20"
                onClick={() => setLightboxImage(null)}
              >
                <X className="w-5 h-5" />
              </Button>
              
              <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
                <div className="flex items-center gap-4 text-white text-sm">
                  <span className="flex items-center gap-1">
                    <User className="w-4 h-4" />
                    {lightboxImage.uploaded_by_name}
                  </span>
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {format(parseISO(lightboxImage.uploaded_at), "d MMM yyyy, HH:mm", { locale: dateLocale })}
                  </span>
                </div>
              </div>
              
              {!readOnly && (
                <Button
                  variant="destructive"
                  size="sm"
                  className="absolute top-2 left-2"
                  onClick={() => {
                    handleDelete(lightboxImage.id)
                    setLightboxImage(null)
                  }}
                  disabled={deletingId === lightboxImage.id}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  {lang === 'es' ? 'Eliminar' : 'Delete'}
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
